/**
 * imageCompression.js
 * Client-side image compression utility.
 * - Auto-rotates based on EXIF via createImageBitmap with imageOrientation:'from-image'
 * - Resizes so LONGEST side is maxSize px (preserves aspect ratio for portrait AND landscape)
 * - Encodes as JPEG at given quality
 * - Skips re-compression if file is already small enough
 * - No external dependencies
 */

/**
 * Get natural dimensions of a Blob/File via an Image element.
 * @param {Blob} blob
 * @returns {Promise<{width: number, height: number}>}
 */
export async function getImageDimensions(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for dimension check'));
    };
    img.src = url;
  });
}

/**
 * Compress an image file.
 * @param {File} file - Input image file (JPEG/PNG/WEBP/HEIC/HEIF)
 * @param {{ maxSize?: number, quality?: number }} options
 * @returns {Promise<Blob>} Compressed JPEG blob (or original blob if already small)
 */
export async function compressImage(file, { maxSize = 1200, quality = 0.82 } = {}) {
  // Skip if already small enough
  const MAX_SKIP_BYTES = 300 * 1024; // 300 KB

  // Try createImageBitmap with EXIF orientation (Chrome/Safari 15+)
  let bitmap = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Fallback: createImageBitmap without orientation option
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      bitmap = null;
    }
  }

  if (!bitmap) {
    // Final fallback: plain Image element
    return await _compressViaImageElement(file, maxSize, quality, MAX_SKIP_BYTES);
  }

  const origW = bitmap?.width;
  const origH = bitmap?.height;
  const longestSide = Math.max(origW, origH);

  // Skip compression if already small and within size limit
  if (file?.size < MAX_SKIP_BYTES && longestSide <= maxSize) {
    bitmap?.close?.();
    return file;
  }

  // Compute target dimensions preserving aspect ratio
  let targetW = origW;
  let targetH = origH;
  if (longestSide > maxSize) {
    const scale = maxSize / longestSide;
    targetW = Math.round(origW * scale);
    targetH = Math.round(origH * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas?.getContext('2d');
  ctx?.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap?.close?.();

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          console.info(
            `[imageCompression] ${file.name}: ${(file.size / 1024).toFixed(0)} KB → ${(blob.size / 1024).toFixed(0)} KB (${targetW}×${targetH})`
          );
          resolve(blob);
        } else {
          reject(new Error('canvas.toBlob returned null'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Fallback compression using a plain Image element (no EXIF rotation).
 */
async function _compressViaImageElement(file, maxSize, quality, skipBytes) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const origW = img.naturalWidth;
      const origH = img.naturalHeight;
      const longestSide = Math.max(origW, origH);

      if (file.size < skipBytes && longestSide <= maxSize) {
        resolve(file);
        return;
      }

      let targetW = origW;
      let targetH = origH;
      if (longestSide > maxSize) {
        const scale = maxSize / longestSide;
        targetW = Math.round(origW * scale);
        targetH = Math.round(origH * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, targetW, targetH);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            console.info(
              `[imageCompression] fallback ${file.name}: ${(file.size / 1024).toFixed(0)} KB → ${(blob.size / 1024).toFixed(0)} KB`
            );
            resolve(blob);
          } else {
            reject(new Error('canvas.toBlob returned null (fallback)'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image load failed in fallback compressor'));
    };
    img.src = url;
  });
}
